import { getConfigForImportedTest, getConfigForMeasuredTest } from 'Components/Worksheets/Config';
import { IConfigFunctionArgs } from 'Components/Worksheets/Core';
import { mergeConfig } from 'Components/Worksheets/Core/Helpers/configHelpers';
import { FormatTypes } from 'Lib/Format/formatType';
import * as oPath from '../../../../../Lib/Object/Path/Immutable';
import qestIds from '../../../QestIds';
import getDerivedFromMeasurementsConfig from '../../derivedFromMeasurementsConfig';
import getMeasuredUsingConfig from '../../measuredUsingConfig';
import getLabelConfig from './labelConfig';

const getHammerFaceLengthConfig = (worksheet: any) => {
  const units = oPath.get(worksheet, `data.Equipment.HammerFaceNominalUnits`);
  return {
    format: units === 'Inch-Pound' ? { nearest: 0.001 } : { nearest: 0.01 },
    uom: units === 'Inch-Pound' ? 'in' : 'mm',
  };
};

const mouldVolumeConfig = { uom: 'cm³', format: { nearest: 0.1 } };
const waterAddedPercentConfig = { uom: '%', format: { nearest: 0.1 } };
const wetDensityConfig = {
  uom: 't/m³',
  format: { type: FormatTypes.SignificantFigures, precision: 4 },
};
const convertedWetDensityConfig = {
  uom: 't/m³',
  format: { nearest: 0.01 },
};

const config = (_: string) =>
  mergeConfig(
    {
      'data.Equipment.MouldVolume': { uom: 'cm³', format: { nearest: 0.1 } },
      'data.Equipment.HammerFaceDiameter': (configArgs: IConfigFunctionArgs) =>
        getHammerFaceLengthConfig(configArgs.worksheet),
      'data.Equipment.HammerFaceRadius': (configArgs: IConfigFunctionArgs) =>
        getHammerFaceLengthConfig(configArgs.worksheet),
      ...getConfigForMeasuredTest(
        qestIds.oversizeDetermination.AS_1289_5_1_1,
        'data.Preparation.AssociatedOversizeDetermination'
      ),

      'data.OversizeCorrectionData.OversizeVolume': { uom: 'cm³', format: { nearest: 0.1 } },
      'data.OversizeCorrectionData.OversizeDensity': { uom: 't/m³', format: { nearest: 0.01 } },
      'data.TestPortions.MouldVolume': mouldVolumeConfig,
      'data.TestPortions.WaterAddedTargetPercent': { format: { nearest: 1 } },
      'data.TestPortions.WaterAddedPercent': waterAddedPercentConfig,
      'data.TestPortions.WetDensity': wetDensityConfig,
      'data.TestPortions.ConvertedWetDensity': convertedWetDensityConfig,

      'cwdChart.XAxisMajorStep': { format: { nearest: 0.1 } },
      'cwdChart.XAxisMin': { format: { nearest: 0.1 } },

      'data.FieldResults.FieldWetDensity': { uom: 't/m³', format: { nearest: 0.01 } },
      'data.FieldResults.AssociatedFieldWetDensity.ExternalTest.Results.FieldWetDensity': {
        uom: 't/m³',
        format: { nearest: 0.01 },
      },
      'data.FieldResults.FieldDryDensity': {
        uom: 't/m³',
        format: { nearest: 0.01 },
      },
      'data.FieldResults.AssociatedFieldMoistureContent.ExternalTest.Results.MoistureContentPercent': {
        format: { nearest: 0.1 },
      },
      'data.FieldResults.FieldMoistureContent': { format: { nearest: 0.1 } },
      ...getConfigForImportedTest(
        qestIds.moistureContent.AS_1289_2_1_1.rev2005,
        'data.FieldResults.AssociatedFieldMoistureContent',
        [qestIds.nuclearFieldDensity.AS_1289_5_8_1.rev2007]
      ),
      ...getConfigForImportedTest(
        qestIds.nuclearFieldDensity.AS_1289_5_8_1.rev2007,
        'data.FieldResults.AssociatedFieldWetDensity'
      ),
      'data.Results.MoistureCorrection': { format: { nearest: 0.1 } },
      'data.Results.MoistureVariation': { format: { nearest: 0.1 } },
      'data.Results.AdjustedMoistureVariation': { format: { nearest: 0.1 } },
      'data.Results.OptimumMoistureContent': { format: { nearest: 0.1 } },
      'data.Results.PeakMoistureIncrement': { format: { nearest: 0.1 } },
      'data.Results.MoistureRatio': { format: { nearest: 0.1 } },
      'data.Results.HilfDensityRatio': { format: { nearest: 0.1 } },
      'data.Results.PeakConvertedWetDensity': convertedWetDensityConfig,
      'data.Results.AdjustedPeakConvertedWetDensity': convertedWetDensityConfig,
    },
    getLabelConfig('data'),
    getMeasuredUsingConfig('data'),
    getDerivedFromMeasurementsConfig('data'),
    { [`data.Equipment.CompactionBalance`]: { defaultReadability: 1, defaultUom: 'g' } }
  );

export default {
  [qestIds.convertedWetDensity.AS_1289_5_7_1.rev2006]: config,
};
